import React from 'react';
import styled, { css } from 'styled-components/macro';
import { rgba } from 'polished';
import { NavLink } from 'react-router-dom';

import { ReactComponent as ToggleCaret } from 'icons/icon-toggle-caret.svg';
import Box from 'components/elements/Box';

const fontStyles = css`
  font-weight: 600;
`;

export const Nav = styled.nav<any>(
  ({ theme, showBackdrop }) => css`
    position: relative;

    @media screen and (min-width: 50em) and (max-width: 62em) {
      font-size: 0.8em;

      &::before {
        top: 2.5em;
      }
    }

    &::before {
      display: ${showBackdrop ? 'block' : 'none'};
      position: absolute;
      content: '';
      top: 3.85em;
      left: 0;
      width: 100%;
      height: 15em;
      background: ${theme.SubNav};
    }
  `
);

export const Button = styled.button(
  ({ theme }) => css`
    background: transparent;
    border-top: 1px solid rgba(255, 255, 255, 0.2);
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 ${theme.spacing.xlarge};
    ${fontStyles};

    &:active {
      background: rgba(255, 255, 255, 0.1);
    }
  `
);

export const ActiveTopic = styled.span<any>(
  ({ theme, isOpen }) => css`
    border-bottom: 2px solid ${isOpen ? 'transparent' : theme.AccentGreen};
    color: ${isOpen ? 'white' : theme.AccentGreen};
    padding: ${theme.spacing.medium} 0;
    margin: 0 auto;
    font-size: 18px;

    @media screen and (min-width: 50em) {
      border-bottom: 4px solid ${theme.AccentGreen};
    }

    ${Box} {
      > span {
        color: ${theme.White};
        margin-left: ${theme.spacing.small};
      }
    }
  `
);

const ToggleCaretWrapper = (props: any): JSX.Element => {
  const { children, ...rest } = props;
  delete rest.isPointingUp;

  return <ToggleCaret {...rest}>{children}</ToggleCaret>;
};

export const CaretIcon = styled(ToggleCaretWrapper)`
  transform: ${({ isPointingUp }): any =>
    isPointingUp ? 'rotate(180deg)' : 'rotate(0deg)'};
  transition: transform 300ms linear;
  color: white;
`;

export const List = styled.ul(
  ({ theme }) => css`
    position: absolute;
    width: 100%;
    display: flex;
    flex-direction: column;

    > li {
      border-bottom: 1px solid ${rgba(theme.CasperGrey, 0.25)};
    }

    @media screen and (min-width: 50em) {
      flex-direction: row;
      justify-content: space-around;
      position: static;

      > li {
        border-bottom: none;
      }
    }

    @media screen and (max-width: 799px) {
      background: ${theme.SubNav};
    }
  `
);

const NavLinkWrapper = (props: any): JSX.Element => {
  const { className, children, ...rest } = props;
  delete rest.active;
  return (
    <NavLink className={className} {...rest}>
      {children}
    </NavLink>
  );
};

export const Link = styled(NavLinkWrapper)(
  ({ theme }) => css`
    ${fontStyles};
    padding: ${theme.spacing.medium} ${theme.spacing.xlarge};
    color: white;
    display: block;

    &.active {
      color: ${theme.AccentGreen};
    }
    &:hover,
    &:focus {
      background: rgba(255, 255, 255, 0.05);
    }
    &:active {
      background: rgba(255, 255, 255, 0.1);
    }
  `
);

export const StyledSubList = styled.ul`
  width: 100%;
  display: flex;
  flex-direction: column;
  padding: ${(p): string =>
    `0 ${p.theme.spacing.large} ${p.theme.spacing.large}`};

  ${Link} {
    padding: ${(p): string =>
      `${p.theme.spacing.small}  ${p.theme.spacing.xlarge}`};
    text-align: center;
  }
`;

export const DesktopLink = styled(Link)(
  ({ theme, active }) => css`
    white-space: nowrap;
    position: relative;
    padding: ${theme.spacing.medium} ${theme.spacing.medium} 0;

    display: flex;
    justify-content: center;
    align-items: center;

    ${Box} {
      padding-bottom: ${theme.spacing.medium};
      border-bottom: 3px solid ${active ? theme.AccentGreen : 'transparent'};
      align-items: center;
      color: ${theme.White};

      > span {
        margin-left: ${theme.spacing.small};
      }
    }
  `
);

export const DesktopSubList = styled.ul(
  ({ theme }) => css`
    text-align: center;
    position: absolute;
    width: 100%;
    height: 15em;
    top: 3.85em;
    padding: 0 ${theme.spacing.medium};
    padding-top: 0.5em;

    &:hover {
      background: rgba(255, 255, 255, 0.05);

      + ${DesktopLink} {
        background: rgba(255, 255, 255, 0.05);
      }
    }
  `
);

export const ListItem = styled.li`
  position: relative;
  flex-grow: 1;
  text-align: center;

  &:hover {
    ${DesktopSubList} {
      background: rgba(255, 255, 255, 0.05);
    }
  }
`;

export const SubLink = styled(NavLink)(
  ({ theme }) => css`
    ${fontStyles}
    padding: ${theme.spacing.xsmall} ${theme.spacing.small};
    margin: ${theme.spacing.xxsmall} 0;
    color: white;
    display: block;

    &:hover,
    &:focus {
      background: ${rgba(theme.MenuActive, 0.4)};
    }
    &:active {
      background: ${rgba(theme.MenuActive, 0.5)};
    }

    &.active {
      color: ${theme.AccentGreen};
      background: ${rgba(theme.MenuActive, 0.4)};
    }
  `
);

export const SubButton = styled.button<any>(
  ({ theme }) => css`
    ${fontStyles};
    padding: ${theme.spacing.medium} ${theme.spacing.xlarge}
      ${theme.spacing.medium} ${theme.spacing.large};
    color: ${theme.White};
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    position: relative;

    > span {
      margin-left: ${theme.spacing.small};
      font-size: 18px;
    }

    ${CaretIcon} {
      position: absolute;
      right: ${theme.spacing.xlarge};
    }
  `
);
